import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { log } from 'src/app/utils/logging';
import { actionUpdateUserPermissions } from 'src/app/v1/state/modules/session/userPermissions';
import { useGetUserPermissionsLazyQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useUserPermissions = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const { advertiserId, isDataCleanupComplete, userId } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      isDataCleanupComplete: state.app.isDataCleanupComplete,
      userId: state.session.userId,
    }),
    shallowEqual,
  );

  const [getUserPermissionsLazyQuery] = useGetUserPermissionsLazyQuery({
    onCompleted: (data) => {
      const canCreateJobs =
        data?.hirerAccountUser?.permissions?.canCreatePosition;
      const canEditJobs =
        data?.hirerAccountUser?.permissions?.hasAllPositionAccess;

      if (
        typeof canCreateJobs === 'boolean' &&
        typeof canEditJobs === 'boolean'
      ) {
        dispatch(
          actionUpdateUserPermissions({
            canCreateJobs,
            canEditJobs,
          }),
        );
      } else {
        log.error(
          `useUserPermissions | Failed to get permissions`,
          {
            userId,
            advertiserId,
            canCreateJobs,
            canEditJobs,
          },
          true,
        );
      }

      setLoading(false);
    },
    onError: (error) => {
      log.error('useUserPermissions | useUserPermissionsLazyQuery error', {
        error,
        userId,
        advertiserId,
      });
      setLoading(false);
    },
  });

  useEffect(() => {
    const asyncFn = async () => {
      setLoading(true);
      getUserPermissionsLazyQuery({ variables: { userId } });
    };

    if (isDataCleanupComplete && userId) {
      asyncFn();
    }
  }, [userId, getUserPermissionsLazyQuery, isDataCleanupComplete]);

  return {
    loading,
  };
};
